import {
  shallowEqual, useDispatch, useSelector
} from 'react-redux';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import { Provider } from './context';
import useAudioElement from '@/hooks/audio/useAudioElement';
import { audioTimeUpdate as audioTimeUpdateInStore, seekAudio as seekAudioInStore } from '@/state/audio/actions';
import {
  selectAudioReadyForSeek,
  selectAudioSeekOverrideKey,
  selectResumeFromSeek
} from '@/state/selectors/audio';

const selector = (state) => ({
  seekOverrideKey: selectAudioSeekOverrideKey(state),
  resumeFromSeek: selectResumeFromSeek(state),
  audioReadyForSeek: selectAudioReadyForSeek(state)
});

const AudioSeekProvider = ({ children }) => {
  const dispatch = useDispatch();
  const {
    seekOverrideKey,
    resumeFromSeek,
    audioReadyForSeek
  } = useSelector(selector, shallowEqual);
  const { audioRef, audioRefReady } = useAudioElement();

  const haltTimeUpdates = useRef();
  const seekOverrideKeyRef = useRef();
  const [seek, setSeek] = useState(0);

  // FUNCTIONS
  // -------------------------------
  const seekAudio = useCallback((newSeek) => dispatch(seekAudioInStore({ seek: newSeek })), [dispatch]);
  const timeUpdate = useCallback((newSeek) => dispatch(audioTimeUpdateInStore({ seek: newSeek })), [dispatch]);

  const timeUpdateThrottled = useCallback(throttle((newSeek) => {
    timeUpdate(newSeek);
  }, 2500), [timeUpdate]);

  const handleSeek = useCallback((newSeek) => {
    timeUpdateThrottled.cancel();
    seekAudio(newSeek);
    setSeek(newSeek);
  }, [timeUpdateThrottled, seekAudio]);

  const handleSeekForward15Seconds = useCallback(() => {
    const newSeek = Math.min(audioRef.current.currentTime + 15, audioRef.current.duration);
    handleSeek(newSeek);
  }, [audioRef, handleSeek]);

  const handleSeekBackward15Seconds = useCallback(() => {
    const newSeek = Math.max(audioRef.current.currentTime - 15, 0);
    handleSeek(newSeek);
  }, [audioRef, handleSeek]);

  // AUDIO TAG LISTENERS
  // -------------------------------
  // WITHIN THESE WE SHOULD UPDATE OUR OWN STATE BASED ON CHANGES
  // TO THE AUDIO TAG
  // -------------------------------
  const audioOnTimeUpdate = useCallback(() => {
    const newSeek = audioRef.current.currentTime;

    if (newSeek && !haltTimeUpdates.current) {
      setSeek(newSeek);
      timeUpdateThrottled(newSeek);
    }
  }, [audioRef, timeUpdateThrottled]);
  
  const audioOnSeeking = useCallback(() => {

  }, []);

  const audioOnSeeked = useCallback(() => {

  }, []);

  // REDUX STATE LISTENERS
  // -------------------------------
  // WITHIN THESE EFFECTS WE SHOULD UPDATE THE AUDIO TAG BASED ON STATE CHANGES
  // -------------------------------

  // If the seek override key has iterated, we should override the audio element seek with that from state
  useEffect(() => {
    if (
      audioRefReady
        && (!seekOverrideKeyRef.current || (seekOverrideKey !== seekOverrideKeyRef.current))
    ) {
      timeUpdateThrottled.cancel();
      setSeek(resumeFromSeek);
      haltTimeUpdates.current = true;

      if (audioReadyForSeek) {
        seekOverrideKeyRef.current = seekOverrideKey;
        audioRef.current.currentTime = resumeFromSeek;
        haltTimeUpdates.current = false;
      }
    }
  }, [
    audioRef,
    audioRefReady,
    seekOverrideKey,
    resumeFromSeek,
    audioReadyForSeek,
    timeUpdateThrottled,
    seekAudio
  ]);

  // Attach event listener to the audio element
  useEffect(() => {
    const au = audioRef.current;

    if (audioRefReady) {
      au.addEventListener('timeupdate', audioOnTimeUpdate);
      au.addEventListener('seeking', audioOnSeeking);
      au.addEventListener('seeked', audioOnSeeked);
    }

    return () => {
      if (audioRefReady) {
        au.removeEventListener('timeupdate', audioOnTimeUpdate);
        au.removeEventListener('seeking', audioOnSeeking);
        au.removeEventListener('seeked', audioOnSeeked);
      }
    };
  }, [
    audioRef,
    audioRefReady,
    audioOnTimeUpdate,
    audioOnSeeking,
    audioOnSeeked
  ]);

  const value = useMemo(() => ({
    seek,
    handleSeek,
    handleSeekBackward15Seconds,
    handleSeekForward15Seconds
  }), [seek, handleSeek, handleSeekBackward15Seconds, handleSeekForward15Seconds]);

  return (
    <Provider value={ value }>
      { children }
    </Provider>
  );
};

AudioSeekProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default AudioSeekProvider;